/**
 * src/components/agent/VoiceOrb.jsx — Animated orb for Voice Mode (Phase 82-05).
 * Modes: blob (default), bars (amplitude), dot. Phase drives tempo. Respects
 * prefers-reduced-motion. Horizontal swipe cycles orbMode.
 */

import { useRef, useEffect, useState, useMemo } from 'react';

const BASE_SIZE = 160;
const DRIVING_SCALE = 1.5;

function usePrefersReducedMotion() {
  const [reduced, setReduced] = useState(false);
  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return undefined;
    const mq = window.matchMedia('(prefers-reduced-motion: reduce)');
    const apply = () => setReduced(mq.matches);
    apply();
    if (typeof mq.addEventListener === 'function') {
      mq.addEventListener('change', apply);
      return () => mq.removeEventListener('change', apply);
    }
    mq.addListener(apply);
    return () => mq.removeListener(apply);
  }, []);
  return reduced;
}

function useSwipe(ref, onSwipe) {
  useEffect(() => {
    const el = ref.current;
    if (!el) return undefined;
    let startX = null;
    const onStart = (e) => { startX = (e.touches?.[0] ?? e).clientX; };
    const onEnd = (e) => {
      if (startX == null) return;
      const endX = (e.changedTouches?.[0] ?? e).clientX;
      const dx = endX - startX;
      startX = null;
      if (Math.abs(dx) > 48) onSwipe?.();
    };
    el.addEventListener('touchstart', onStart, { passive: true });
    el.addEventListener('touchend', onEnd, { passive: true });
    el.addEventListener('pointerdown', onStart);
    el.addEventListener('pointerup', onEnd);
    return () => {
      el.removeEventListener('touchstart', onStart);
      el.removeEventListener('touchend', onEnd);
      el.removeEventListener('pointerdown', onStart);
      el.removeEventListener('pointerup', onEnd);
    };
  }, [ref, onSwipe]);
}

function BlobOrb({ phase, reduced, audioLevel }) {
  const scale = reduced
    ? 1
    : phase === 'speaking'
      ? 1 + Math.min(0.15, audioLevel * 0.15)
      : phase === 'thinking'
        ? 1.04
        : phase === 'paused'
          ? 1
          : 1.02;
  const duration = phase === 'thinking' ? '800ms' : phase === 'speaking' ? '600ms' : '1200ms';
  return (
    <div
      data-testid="voice-orb-blob"
      style={{
        width: '100%',
        height: '100%',
        borderRadius: '48%',
        background: 'radial-gradient(circle at 30% 30%, var(--accent), var(--blue))',
        transform: `scale(${scale})`,
        transition: reduced ? 'none' : `transform ${duration} var(--ease)`,
        opacity: phase === 'paused' ? 0.6 : 1,
        boxShadow: '0 16px 48px rgba(28,63,117,0.35)',
      }}
    />
  );
}

function BarsOrb({ phase, reduced, audioLevel }) {
  const bars = [0, 1, 2, 3, 4];
  return (
    <div
      data-testid="voice-orb-bars"
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 8,
        height: '100%',
      }}
    >
      {bars.map((i) => {
        const base = 24;
        const active = phase === 'speaking' ? audioLevel : phase === 'listening' ? 0.35 : phase === 'thinking' ? 0.55 : 0.2;
        const variance = reduced ? 0 : Math.sin((i * 97 + Date.now() / 80) * 0.01) * 10;
        const height = base + active * 80 + variance;
        return (
          <div
            key={i}
            style={{
              width: 12,
              height: Math.max(8, height),
              background: 'var(--accent)',
              borderRadius: 6,
              transition: reduced ? 'none' : 'height 120ms var(--ease)',
            }}
          />
        );
      })}
    </div>
  );
}

function DotOrb({ phase, reduced }) {
  const scale = reduced ? 1 : phase === 'thinking' ? 1.1 : phase === 'speaking' ? 1.2 : 1;
  return (
    <div
      data-testid="voice-orb-dot"
      style={{
        width: 56,
        height: 56,
        borderRadius: '50%',
        background: 'var(--accent)',
        transform: `scale(${scale})`,
        transition: reduced ? 'none' : 'transform 400ms var(--ease)',
        margin: 'auto',
      }}
    />
  );
}

export default function VoiceOrb({
  phase = 'listening',
  orbMode = 'blob',
  audioLevel = 0.2,
  drivingMode = false,
  onModeCycle,
}) {
  const containerRef = useRef(null);
  const reduced = usePrefersReducedMotion();
  useSwipe(containerRef, onModeCycle);

  const size = useMemo(() => (drivingMode ? BASE_SIZE * DRIVING_SCALE : BASE_SIZE), [drivingMode]);

  const Inner = orbMode === 'bars' ? BarsOrb : orbMode === 'dot' ? DotOrb : BlobOrb;

  return (
    <div
      ref={containerRef}
      role="img"
      aria-label={`Voice ${phase}`}
      data-testid="voice-orb"
      data-phase={phase}
      data-mode={orbMode}
      data-reduced-motion={reduced ? 'true' : 'false'}
      style={{
        width: size,
        height: size,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        position: 'relative',
        margin: '0 auto',
        touchAction: 'pan-y',
        userSelect: 'none',
      }}
    >
      <Inner phase={phase} reduced={reduced} audioLevel={audioLevel} />
      {phase === 'thinking' && !reduced && (
        <div
          data-testid="voice-orb-ring"
          style={{
            position: 'absolute',
            width: size * 1.2,
            height: size * 1.2,
            borderRadius: '50%',
            border: '2px solid var(--accent)',
            opacity: 0.4,
            pointerEvents: 'none',
            animation: 'fadeIn 800ms var(--ease) infinite alternate',
          }}
        />
      )}
    </div>
  );
}
